import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { SchedulerEvent } from '../../../../models/SchedulerEvent';
import { Setting } from '../../../../models/Setting';
import { TranslatePipe } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';
import { RadioButton } from 'primeng/radiobutton';
import { SettingValuePipe } from '../../../../pipes/setting-value.pipe';
import { SanitizeHtmlPipe } from '../../../../pipes/sanitize-html.pipe';
import { ButtonDirective } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { SchedulerEventComponent } from '../../../schedule/event/schedulerEvent.component';
import { SettingService } from '../../../../services/setting.service';
import { ScheduleService } from '../../../../services/schedule.service';
import { AuthService } from '../../../../services/auth.service';

import { AppDatePipe } from '../../../../pipes/app-date.pipe';
@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],

  imports: [
    TranslatePipe,
    AppDatePipe,
    FormsModule,
    RadioButton,
    SettingValuePipe,
    SanitizeHtmlPipe,
    ButtonDirective
  ]
})
export class EventComponent implements OnInit, OnChanges {
  @Input() event: SchedulerEvent | null = null;
  @Input() roomSettings: Array<Setting> | undefined = undefined;
  public statusOptions: Array<Setting> = [];
  public selectedStatus: string | null = null;
  public inProgressByName: string | null = null;
  public inProgressAt: Date | null = null;
  public completedByName: string | null = null;
  public completedAt: Date | null = null;
  private savingStatus = false;

  constructor(
    private dialogService: DialogService,
    private settingService: SettingService,
    private scheduleService: ScheduleService,
    private authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    this.statusOptions =
      await this.settingService.getSettingByName('EVENT_TODO_STATUS');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.event) {
      this.selectedStatus =
        this.event &&
        this.event.status !== undefined &&
        this.event.status !== null
          ? String(this.event.status)
          : null;
      this.populateStatusInfo();
    }
  }

  /**
   * Reads the persisted "who + when" stamps (set on the status transitions and
   * shared with everyone via EVENT_V) so they can be shown next to the
   * Tekeillä (In progress) and Valmis (Done) options.
   */
  private populateStatusInfo(): void {
    this.inProgressByName = this.event?.inProgressByName || null;
    this.inProgressAt = this.event?.inProgressAt
      ? new Date(this.event.inProgressAt)
      : null;
    this.completedByName = this.event?.completedByName || null;
    this.completedAt = this.event?.completedAt
      ? new Date(this.event.completedAt)
      : null;
  }

  /**
   * Saves the new status as soon as a Tila radio is chosen, and refreshes the
   * event lists. Other details (person/group) are edited via "Muokkaa tehtävää".
   */
  async onStatusChange(code: string): Promise<void> {
    if (!this.event || this.savingStatus) {
      return;
    }
    const newStatus = Number(code);
    if (newStatus === this.event.status) {
      return;
    }
    this.savingStatus = true;
    const updated: SchedulerEvent = { ...this.event, status: newStatus };
    try {
      const saved = await this.scheduleService.saveSchedulerEvent(updated);
      if (saved) {
        this.event.status = newStatus;
        // Show the current user as the doer right away (the backend persists the
        // same; a reload then shows it to everyone via EVENT_V).
        const profile = this.authService.getUserProfile();
        const name = profile
          ? `${profile.firstName} ${profile.lastName}`.trim()
          : null;
        if (newStatus === 1) {
          this.inProgressByName = name;
          this.inProgressAt = new Date();
        } else if (newStatus === 3) {
          this.completedByName = name;
          this.completedAt = new Date();
        }
        this.scheduleService.notifyEventsChanged();
      } else {
        this.selectedStatus = String(this.event.status);
      }
    } catch (error) {
      this.selectedStatus = String(this.event.status);
    } finally {
      this.savingStatus = false;
    }
  }

  /**
   * Opens the full task dialog for editing all details of the todo.
   */
  editTask(): void {
    if (!this.event) {
      return;
    }
    this.dialogService.open(SchedulerEventComponent, {
      header: 'Todo',
      data: {
        eventData: this.event,
        eventInfo: null
      },
      width: '75%',
      height: '75%',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });
  }
}
